import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { loadFirstRunState, onFirstRunChanged } from "@/shared/onboarding/first-run";
import {
  hasStoredPreferences,
  loadPreferences,
  onPreferencesChanged,
} from "@/shared/preferences/store";
import { documentLanguageMetadata } from "./document-language.mjs";
import { isAppExperiencePath, shouldUseStoredExperience } from "./experience-precedence.mjs";

function applyDocumentLanguage(lang: string, country: string) {
  const metadata = documentLanguageMetadata(lang, country);
  document.documentElement.lang = metadata.lang;
  document.documentElement.dir = metadata.dir;
}

function applyPersistedDocumentLanguage() {
  const firstRun = loadFirstRunState();
  const onboardingPreviewActive = window.location.pathname === "/onboarding";
  const usePreferences = shouldUseStoredExperience({
    preferFirstRun: onboardingPreviewActive,
    preferStoredExperience: isAppExperiencePath(window.location.pathname),
    firstRunCompleted: firstRun.completed,
    storedPreferencesAvailable: hasStoredPreferences(),
  });
  const experience = usePreferences ? loadPreferences() : firstRun;
  applyDocumentLanguage(experience.lang, experience.country);
}

export function useDocumentLanguageOverride(lang: string, country: string) {
  useEffect(() => {
    applyDocumentLanguage(lang, country);
    return applyPersistedDocumentLanguage;
  }, [country, lang]);
}

export function DocumentLanguage() {
  const location = useLocation();

  useEffect(() => {
    applyPersistedDocumentLanguage();
    const stopFirstRun = onFirstRunChanged(applyPersistedDocumentLanguage);
    const stopPreferences = onPreferencesChanged(applyPersistedDocumentLanguage);

    return () => {
      stopFirstRun();
      stopPreferences();
    };
  }, [location.pathname]);

  return null;
}
